/**
 * Distribution screen — Attempts tab (task 005-08, ADR-010 §2 / BRD FR-B21).
 *
 * The attempt log ops reads: campaign / outcome / kind / email / date filters,
 * an "Include test" toggle (default on — test rows are part of the ops log,
 * task 005-07's API default), the log table and a per-row drill-down dialog
 * with the masked request summary, the response excerpt and the retained raw
 * partner response. Read-only — attempts are written by the worker only.
 *
 * List conventions (admin-requirements.md §1.4): plain-language headers,
 * MM-DD-YYYY dates, no DB ids in the table. The job id — the handle ops quotes
 * when chasing a dead letter — appears in the drill-down dialog only.
 */

import { useEffect, useState, type ReactNode } from 'react';
import {
  type AttemptKind,
  type AttemptOutcome,
  type DistributionAttemptDetailResponse,
  type DistributionAttemptRowResponse,
} from '@leads-platform/shared';
import { api, ApiError, type CampaignDetailResponse } from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Tag } from '../../components/ui/tag';
import { Select } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Toggle } from '../../components/ui/toggle';
import { SearchInput } from '../../components/ui/search-input';
import { DateRangeField } from '../../components/date-range-field';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { fmtDateTime } from '../../lib/format';
import {
  getDistributionAttempt,
  getDistributionAttempts,
  type DistributionAttemptsFilters,
} from './api';
import { WhenCell } from './when';

/** Outcome → Tag tone (task scope): accepted sky · duplicate amber · rejected
 * rose · error/timeout rose · skipped neutral. `suppressed` is an ESP-only
 * outcome (A8 (d)) the attempt vocabulary still admits — neutral. */
const OUTCOME_TONES: Record<AttemptOutcome, 'sky' | 'amber' | 'rose' | 'neutral'> = {
  accepted: 'sky',
  duplicate: 'amber',
  rejected: 'rose',
  suppressed: 'neutral',
  error: 'rose',
  timeout: 'rose',
  skipped: 'neutral',
};

/** Plain-language outcome labels (§1.4) — also the filter's option list. */
const OUTCOME_LABELS: Record<AttemptOutcome, string> = {
  accepted: 'Accepted',
  duplicate: 'Duplicate',
  rejected: 'Rejected',
  suppressed: 'Suppressed',
  error: 'Error',
  timeout: 'Timeout',
  skipped: 'Skipped',
};

/** Plain-language kind labels. */
const KIND_LABELS: Record<AttemptKind, string> = {
  pre_ping: 'Pre-ping',
  send: 'Send',
};

/** `<select>` values are plain strings — narrow one back to the enum
 * (undefined for "All"). Explicit literals, no cast. */
const OUTCOME_BY_VALUE: Record<string, AttemptOutcome | undefined> = {
  accepted: 'accepted',
  duplicate: 'duplicate',
  rejected: 'rejected',
  suppressed: 'suppressed',
  error: 'error',
  timeout: 'timeout',
  skipped: 'skipped',
};

const KIND_BY_VALUE: Record<string, AttemptKind | undefined> = {
  send: 'send',
  pre_ping: 'pre_ping',
};

// --- Date presets (the sites screen's month-preset row, mirrored) ---

/** `none` = no date filter (the tab's initial view); `custom` = the picker. */
type DatePreset = 'none' | 'today' | 'last_7_days' | 'this_month' | 'custom';

const DATE_PRESETS: ReadonlyArray<readonly [DatePreset, string]> = [
  ['today', 'Today'],
  ['last_7_days', 'Last 7 days'],
  ['this_month', 'This month'],
  ['custom', 'Custom'],
];

const pad = (n: number) => String(n).padStart(2, '0');

const ymd = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

function todayYmd(): string {
  return ymd(new Date());
}

function last7Range(): [string, string] {
  const start = new Date();
  start.setDate(start.getDate() - 6);
  return [ymd(start), todayYmd()];
}

function thisMonthRange(): [string, string] {
  const now = new Date();
  return [`${ymd(now).slice(0, 7)}-01`, todayYmd()];
}

/** The filter state the tab renders — empty string means "no filter". */
interface AttemptFilterState {
  campaignId: string;
  outcome: string;
  kind: string;
  emailDraft: string;
  email: string;
  preset: DatePreset;
  from: string;
  to: string;
  includeTest: boolean;
}

const INITIAL_FILTERS: AttemptFilterState = {
  campaignId: '',
  outcome: '',
  kind: '',
  emailDraft: '',
  email: '',
  preset: 'none',
  from: '',
  to: '',
  includeTest: true,
};

/** Filter state → the api's query object (empty entries drop out). */
function toQuery(state: AttemptFilterState): DistributionAttemptsFilters {
  return {
    campaignId: state.campaignId || undefined,
    outcome: state.outcome ? OUTCOME_BY_VALUE[state.outcome] : undefined,
    kind: state.kind ? KIND_BY_VALUE[state.kind] : undefined,
    email: state.email || undefined,
    from: state.from || undefined,
    to: state.to || undefined,
    includeTest: state.includeTest,
  };
}

/** Outcome cell — the tone-coded tag with the reason underneath. */
function OutcomeCell({ row }: { row: DistributionAttemptRowResponse }) {
  return (
    <td className="p-4 align-middle">
      <Tag tone={OUTCOME_TONES[row.outcome]}>{OUTCOME_LABELS[row.outcome]}</Tag>
      {row.reason ? <span className="mt-0.5 block text-xs text-faint">{row.reason}</span> : null}
    </td>
  );
}

/** One drill-down field: plain-language label over the value. */
function DetailField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-faint">{label}</div>
      <div className="mt-0.5 text-[13px] text-ink">{children}</div>
    </div>
  );
}

export function AttemptsTab() {
  const [filters, setFilters] = useState<AttemptFilterState>(INITIAL_FILTERS);
  const [campaigns, setCampaigns] = useState<CampaignDetailResponse[]>([]);
  const [rows, setRows] = useState<DistributionAttemptRowResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Drill-down state — one dialog at a time, opened by row click.
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detail, setDetail] = useState<DistributionAttemptDetailResponse | null>(null);

  // Campaign filter options — reference data, loaded once. A failure here
  // must not hide the log: the campaign filter simply stays empty.
  useEffect(() => {
    api
      .getCampaigns()
      .then(setCampaigns)
      .catch(() => {
        // Non-critical — leave the campaign filter empty.
      });
  }, []);

  // The email filter is exact (task scope): commit the trimmed draft after a
  // short pause, so typing one address does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      const email = filters.emailDraft.trim();
      setFilters((prev) => (prev.email === email ? prev : { ...prev, email }));
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.emailDraft]);

  // Every committed filter change refetches the log.
  useEffect(() => {
    setLoading(true);
    getDistributionAttempts(toQuery(filters))
      .then(setRows)
      .catch((err: ApiError) => setError(err.message))
      .finally(() => setLoading(false));
  }, [filters]);

  const patchFilters = (patch: Partial<AttemptFilterState>) =>
    setFilters((prev) => ({ ...prev, ...patch }));

  const applyPreset = (preset: DatePreset) => {
    if (preset === 'today') {
      const today = todayYmd();
      patchFilters({ preset, from: today, to: today });
    } else if (preset === 'last_7_days') {
      const [from, to] = last7Range();
      patchFilters({ preset, from, to });
    } else if (preset === 'this_month') {
      const [from, to] = thisMonthRange();
      patchFilters({ preset, from, to });
    } else {
      patchFilters({ preset });
    }
  };

  const openDetail = (row: DistributionAttemptRowResponse) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    getDistributionAttempt(row.id)
      .then(setDetail)
      .catch((err: ApiError) => setDetailError(err.message))
      .finally(() => setDetailLoading(false));
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(false);
  };

  const summary = detail?.requestSummary;

  return (
    <div>
      <Card className="mb-4 p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-64">
            <Label htmlFor="attempts-campaign" className="mb-1 block text-xs">
              Campaign
            </Label>
            <Select
              id="attempts-campaign"
              value={filters.campaignId}
              onChange={(e) => patchFilters({ campaignId: e.target.value })}
            >
              <option value="">All campaigns</option>
              {campaigns.map((campaign) => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-40">
            <Label htmlFor="attempts-outcome" className="mb-1 block text-xs">
              Outcome
            </Label>
            <Select
              id="attempts-outcome"
              value={filters.outcome}
              onChange={(e) => patchFilters({ outcome: e.target.value })}
            >
              <option value="">All outcomes</option>
              {Object.entries(OUTCOME_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-40">
            <Label htmlFor="attempts-kind" className="mb-1 block text-xs">
              Kind
            </Label>
            <Select
              id="attempts-kind"
              value={filters.kind}
              onChange={(e) => patchFilters({ kind: e.target.value })}
            >
              <option value="">All kinds</option>
              {Object.entries(KIND_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </div>
          <div className="min-w-[240px] flex-1">
            <Label htmlFor="attempts-email" className="mb-1 block text-xs">
              Email
            </Label>
            <SearchInput
              value={filters.emailDraft}
              onChange={(value) => patchFilters({ emailDraft: value })}
              placeholder="Exact email…"
            />
          </div>
          <div>
            <Label className="mb-1 block text-xs">Dates</Label>
            <div className="flex flex-wrap items-center gap-1 pb-1">
              {DATE_PRESETS.map(([preset, label]) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => applyPreset(preset)}
                  className={`rounded-md px-2 py-0.5 text-xs font-medium transition ${
                    filters.preset === preset
                      ? 'bg-accent text-white'
                      : 'bg-paper text-faint hover:text-ink hairline'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {filters.preset === 'custom' && (
              <div className="w-64 pt-1">
                <DateRangeField
                  from={filters.from}
                  to={filters.to}
                  onChange={(from, to) => patchFilters({ from, to })}
                />
              </div>
            )}
          </div>
          <div className="pb-2">
            <Toggle
              checked={filters.includeTest}
              onChange={(checked) => patchFilters({ includeTest: checked })}
              label="Include test"
              hint="Test rows are part of the ops log"
            />
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1080px] text-left text-xs">
            <thead>
              <tr className="border-b border-line text-xs uppercase tracking-wide text-faint">
                <th className="px-4 py-2.5 font-semibold">When</th>
                <th className="px-4 py-2.5 font-semibold">Lead</th>
                <th className="px-4 py-2.5 font-semibold">Campaign</th>
                <th className="px-4 py-2.5 font-semibold">Advertiser</th>
                <th className="px-4 py-2.5 font-semibold">Kind</th>
                <th className="px-4 py-2.5 font-semibold">Outcome</th>
                <th className="px-4 py-2.5 font-semibold">HTTP</th>
                <th className="px-4 py-2.5 font-semibold">Attempt</th>
                <th className="px-4 py-2.5 font-semibold">Test</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-faint">
                    Loading...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-destructive">
                    Error loading attempts: {error}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-faint">
                    No attempts match the current filters.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => openDetail(row)}
                    className="cursor-pointer border-b border-line/70 transition hover:bg-paper/60"
                  >
                    <WhenCell iso={row.startedAt} />
                    <td className="px-4 py-3 font-mono text-xs">{row.leadEmail}</td>
                    <td className="px-4 py-3 text-muted">{row.campaignName}</td>
                    <td className="px-4 py-3 text-muted">{row.advertiserName}</td>
                    <td className="px-4 py-3 text-muted">{KIND_LABELS[row.kind]}</td>
                    <OutcomeCell row={row} />
                    <td className="px-4 py-3 font-mono text-xs text-muted">
                      {row.httpStatus ?? <span className="text-faint">--</span>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted">#{row.attemptNo}</td>
                    <td className="px-4 py-3">
                      {row.isTest ? (
                        <span className="rounded-full bg-amber/10 px-2 py-0.5 text-xs font-medium text-amber">
                          Test
                        </span>
                      ) : (
                        <span className="text-faint">--</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="mt-3 font-mono text-xs text-faint">
        {rows.length} {rows.length === 1 ? 'attempt' : 'attempts'} · newest first · dates MM-DD-YYYY
      </p>

      <Dialog
        open={detailOpen}
        onOpenChange={(open) => {
          if (!open) closeDetail();
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Attempt detail</DialogTitle>
            <DialogDescription>
              {detail
                ? `${KIND_LABELS[detail.kind]} · attempt #${detail.attemptNo} · ${detail.campaignName}`
                : 'Loading…'}
            </DialogDescription>
          </DialogHeader>

          {detailLoading && <p className="text-sm text-faint">Loading…</p>}
          {detailError && (
            <p className="text-sm text-destructive">Error loading attempt: {detailError}</p>
          )}

          {detail && (
            <div className="grid max-h-[70vh] gap-4 overflow-y-auto pr-1">
              <DetailField label="Request">
                {summary?.method || summary?.url ? (
                  <span className="break-all font-mono text-xs">
                    {summary?.method ?? '--'} {summary?.url ?? '--'}
                  </span>
                ) : (
                  <span className="text-faint">
                    No advertiser request — the attempt was skipped
                  </span>
                )}
              </DetailField>

              <DetailField label="Request headers">
                {summary?.headerNames && summary.headerNames.length > 0 ? (
                  <span className="font-mono text-xs">{summary.headerNames.join(', ')}</span>
                ) : (
                  <span className="text-faint">None recorded</span>
                )}
              </DetailField>

              <DetailField label="HTTP status">
                {detail.httpStatus ?? <span className="text-faint">--</span>}
              </DetailField>

              <DetailField label="Response">
                {detail.responseExcerpt ? (
                  <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-md bg-paper p-2 font-mono text-xs text-muted">
                    {detail.responseExcerpt}
                  </pre>
                ) : (
                  <span className="text-faint">None recorded</span>
                )}
              </DetailField>

              {detail.responseLog ? (
                <details>
                  <summary className="cursor-pointer text-xs font-semibold text-accent">
                    Raw response body ({detail.responseLog.body.length} characters)
                  </summary>
                  <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-all rounded-md bg-paper p-2 font-mono text-xs text-muted">
                    {detail.responseLog.body}
                  </pre>
                </details>
              ) : null}

              <div className="grid grid-cols-2 gap-4">
                <DetailField label="Attributed campaign">
                  {detail.attributedCampaignName ?? (
                    <span className="text-faint">unattributed — routed</span>
                  )}
                </DetailField>
                <DetailField label="Routed from">
                  {detail.routedFromAttemptId ? (
                    <span className="font-mono text-xs">Attempt {detail.routedFromAttemptId}</span>
                  ) : (
                    <span className="text-faint">Not routed</span>
                  )}
                </DetailField>
                <DetailField label="Job">
                  {detail.jobId ?? <span className="text-faint">--</span>}
                </DetailField>
                <DetailField label="When">
                  <span className="font-mono text-xs">
                    {fmtDateTime(detail.startedAt)}
                    {detail.finishedAt ? ` → ${fmtDateTime(detail.finishedAt)}` : ''}
                  </span>
                </DetailField>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
